/**
 * A multi-line text control that behaves like `@wend/ui`'s `Input`.
 *
 * It lives here rather than in the design system because slice 2's only new
 * primitive is `Checkbox`, and a component nobody has needed twice does not
 * belong in a shared package yet. It is not a fork of `Input`: it wires itself
 * to a surrounding `<FormField>` through the same exported `useFieldProps`
 * hook the design system offers app-authored controls, so the label, the
 * description, the error and the invalid state all still come from the field.
 *
 * If a second screen wants one, this is the moment to move it into `@wend/ui`
 * — not before.
 */
import { cn, useFieldProps } from '@wend/ui';
import { forwardRef, type TextareaHTMLAttributes } from 'react';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Force the error styling without a surrounding `<FormField>`. */
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, id, invalid, 'aria-describedby': describedBy, 'aria-invalid': ariaInvalid, ...rest },
  ref,
) {
  // An explicit aria-invalid always wins over the field's; `undefined` lets the
  // surrounding FormField decide. Same rule as Input.
  const resolvedInvalid: boolean | undefined =
    ariaInvalid === undefined
      ? invalid === true
        ? true
        : undefined
      : ariaInvalid !== false && ariaInvalid !== 'false';

  const fieldProps = useFieldProps({
    id,
    'aria-describedby': describedBy,
    'aria-invalid': resolvedInvalid,
  });

  return (
    <textarea
      ref={ref}
      className={cn(
        'block w-full resize-y rounded-base border border-line-strong bg-ink/55 px-3.5 py-2.5',
        'text-[0.95rem] leading-normal text-text placeholder:text-muted',
        'transition-[background-color,border-color,box-shadow] duration-150 ease-wend',
        'hover:border-white/25',
        'focus-visible:border-brand-a focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
        'disabled:cursor-not-allowed disabled:opacity-55',
        'aria-[invalid=true]:border-bad aria-[invalid=true]:focus-visible:outline-bad',
        className,
      )}
      {...fieldProps}
      {...rest}
    />
  );
});
